import React from "react";
import { Route, Routes } from "react-router-dom";
import UnauthorizedPage from "@/core/error-pages/403";
import NotFoundPage from "@/core/error-pages/404";
import DashboardHome from "@/routes/DashboardHome";
import ItemsPage from "@/features/items/pages/ItemsPage";
import EducationCatalogPage from "@/features/education-catalog/pages/EducationCatalogPage";
import ClientEducationDetailPage from "@/features/client-educations/pages/ClientEducationDetailPage";
import CreateEducationPage from "@/features/education-catalog/pages/CreateEducationPage";
import EducationManagementPage from "@/features/education-management/pages/EducationManagementPage";
import { CategoriesPage } from "@/features/categories";
import { DepartmentsPage } from "@/features/departments";
import { CompaniesPage } from "@/features/companies";
import LocationsPage from "@/features/locations/pages/LocationsPage";
import { SmsBroadcastPage } from "@/features/sms-broadcast";
import { MailBroadcastPage } from "@/features/mail-broadcast";
import { EducationCalendarPage } from "@/features/education-calendar";
import {
  CreateExamPage,
  EditExamPage,
  ExamsPage,
  ExamsWithDetailPage,
  ExamAttendeesPage,
  ExamAttemptResultPage,
  QuestionPoolPage,
  CreateQuestionPoolPage,
} from "@/features/exam-management";
import {
  CreatePollPage,
  EditPollPage,
  PollsPage,
  PollsWithDetailPage,
  PollAttendeesPage,
} from "@/features/polls";
import {
  StaffManagementPage,
  PersonnelDetailPage,
} from "@/features/staff-management";
import {
  PersonnelGroupsPage,
  PersonnelGroupDetailPage,
} from "@/features/personnel-groups";
import { BlackListPage } from "@/features/black-list";
import {
  RequestAndApprovalPage,
  EducationOfficerPage,
  EducationManagerPage,
} from "@/features/request-and-approval";
import { RolesAndPermissionsPage } from "@/features/roles-and-permissions";
import { SupportTicketsPage } from "@/features/support-tickets";
import { AnnouncementsPage } from "@/features/announcements";
import { LogsPage } from "@/features/logs";
import {
  InternManagementPage,
  InternDetailPage,
} from "@/features/intern-management";
import {
  LibraryPage,
  LibraryFolderDetailPage,
  FoldersPage,
  DocumentsPage,
} from "@/features/library";
import { ComponentsPage } from "@/features/components";
import { ClientEducationsPage } from "@/features/client-educations";
import { ClientEducationRequestsPage } from "@/features/client-education-requests";
import { ClientExamsPage, TakeExamPage } from "@/features/client-exams";
import { ClientAnnouncementsPage } from "@/features/client-announcements";
import { ClientCertificatesPage } from "@/features/client-certificates";
import { ClientPollsPage } from "@/features/client-polls";
import { ClientSupportTicketsPage } from "@/features/client-support-tickets";
import { ClientDashboardPage } from "@/features/client-dashboard";
import {
  CertificatesPage,
  CertificateDetailPage,
  CreateCertificatePage,
} from "@/features/certificates";
import {
  ReportsPage,
  ReportsHomePage,
  ReportsEducationDetailPage,
  CertificatePipelineReportPage,
  CertificateHierarchyReportPage,
  InternProfileReportPage,
  BudgetReportPage,
  DepartmentInvestmentReportPage,
  SurveyNpsReportPage,
  VirtualClassroomReportPage,
  TrainerPerformanceReportPage,
  ComplianceReportPage,
  ExamAnalyticsReportPage,
  LearningPathsReportPage,
  // Genişletme — 12 yeni rapor
  LoginActivityReportPage,
  LibraryConsumptionReportPage,
  CommunicationEffectivenessReportPage,
  SupportSlaReportPage,
  PersonnelDemographicsReportPage,
  LocationPerformanceReportPage,
  AttendanceTrackingReportPage,
  ExamDifficultyReportPage,
  CertificateValidityReportPage,
  RoiInvestmentReportPage,
  EnrollmentFunnelReportPage,
  SurveyCrossTabReportPage,
} from "@/features/reports";
import { CompanyTrainingProfilesPage } from "@/features/company-training-profiles";
import {
  NotificationsPage,
  NotificationPreferencesPage,
} from "@/features/notifications";

const DashboardRoutes: React.FC = () => (
  <Routes>
    <Route element={<DashboardHome />} path="/" />
    <Route element={<ClientDashboardPage />} path="/client-dashboard" />
    <Route element={<ComponentsPage />} path="/components" />
    <Route element={<ClientEducationsPage />} path="/client-educations" />
    <Route
      element={<ClientEducationRequestsPage />}
      path="/client-education-requests"
    />
    <Route element={<ClientExamsPage />} path="/client-exams" />
    <Route element={<TakeExamPage />} path="/client-exams/take/:educationId" />
    <Route element={<ClientCertificatesPage />} path="/client-certificates" />
    <Route element={<ClientAnnouncementsPage />} path="/client-announcements" />
    <Route element={<ClientPollsPage />} path="/client-polls" />
    <Route
      element={<ClientSupportTicketsPage />}
      path="/client-support-tickets"
    />
    <Route element={<ItemsPage />} path="/items" />
    <Route element={<EducationCatalogPage />} path="/education-catalog" />
    <Route
      element={<ClientEducationDetailPage />}
      path="/education-catalog/detail"
    />
    <Route
      element={<CreateEducationPage />}
      path="/education-management/create"
    />
    <Route
      element={<CreateEducationPage />}
      path="/education-management/:educationId/edit"
    />
    <Route element={<EducationManagementPage />} path="/education-management" />
    <Route element={<CategoriesPage />} path="/categories" />
    <Route element={<DepartmentsPage />} path="/departments" />
    <Route element={<CompaniesPage />} path="/companies" />
    <Route element={<LocationsPage />} path="/locations" />
    <Route element={<SmsBroadcastPage />} path="/sms-broadcast" />
    <Route element={<MailBroadcastPage />} path="/mail-broadcast" />
    <Route
      element={<EducationCalendarPage />}
      path="/education-calendar"
    />
    <Route element={<CreateExamPage />} path="/exam-management/create" />
    <Route element={<EditExamPage />} path="/exam-management/:examId/edit" />
    <Route
      element={<ExamsWithDetailPage />}
      path="/exam-management/:examId/detail"
    />
    <Route
      element={<ExamAttemptResultPage />}
      path="/exam-management/:examId/attendees/:attemptId"
    />
    <Route
      element={<ExamAttendeesPage />}
      path="/exam-management/:examId/attendees"
    />
    <Route element={<ExamsPage />} path="/exam-management" />
    <Route element={<QuestionPoolPage />} path="/exam-management/question-pool" />
    <Route element={<CreateQuestionPoolPage />} path="/exam-management/question-pool/create" />
    <Route element={<CreatePollPage />} path="/polls/create" />
    <Route element={<EditPollPage />} path="/polls/:pollId/edit" />
    <Route element={<PollsWithDetailPage />} path="/polls/:pollId/detail" />
    <Route element={<PollAttendeesPage />} path="/polls/:pollId/attendees" />
    <Route element={<PollsPage />} path="/polls" />
    <Route element={<StaffManagementPage />} path="/staff-management" />
    <Route element={<PersonnelGroupsPage />} path="/personnel-groups" />
    <Route element={<PersonnelGroupDetailPage />} path="/personnel-groups/:groupId" />
    <Route
      element={<PersonnelDetailPage />}
      path="/staff-management/:staffId"
    />
    <Route
      element={<RolesAndPermissionsPage />}
      path="/roles-and-permissions"
    />
    <Route element={<BlackListPage />} path="/black-list" />
    <Route element={<RequestAndApprovalPage />} path="/request-and-approval" />
    <Route
      element={<EducationOfficerPage />}
      path="/request-and-approval/education/:educationId/officer"
    />
    <Route
      element={<EducationManagerPage />}
      path="/request-and-approval/education/:educationId/manager"
    />
    <Route element={<AnnouncementsPage />} path="/announcements" />
    <Route element={<CertificatesPage />} path="/certificates" />
    <Route element={<CreateCertificatePage />} path="/certificates/create" />
    <Route
      element={<CertificateDetailPage />}
      path="/certificates/:certificateId"
    />
    <Route element={<LogsPage />} path="/logs" />
    <Route element={<InternManagementPage />} path="/intern-management" />
    <Route element={<InternDetailPage />} path="/intern-management/:internId" />
    {/* Client kütüphane - /dashboard/library */}
    <Route element={<LibraryPage basePath="library" />} path="/library" />
    <Route
      element={<LibraryFolderDetailPage basePath="library" />}
      path="/library/folders/:folderId"
    />
    <Route
      element={<FoldersPage basePath="library" />}
      path="/library/folders"
    />
    <Route
      element={<DocumentsPage basePath="library" />}
      path="/library/documents"
    />
    {/* Admin kütüphane - /dashboard/admin-library */}
    <Route
      element={<LibraryPage basePath="admin-library" />}
      path="/admin-library"
    />
    <Route
      element={<LibraryFolderDetailPage basePath="admin-library" />}
      path="/admin-library/folders/:folderId"
    />
    <Route
      element={<FoldersPage basePath="admin-library" />}
      path="/admin-library/folders"
    />
    <Route
      element={<DocumentsPage basePath="admin-library" />}
      path="/admin-library/documents"
    />
    <Route element={<SupportTicketsPage />} path="/support-tickets" />
    <Route element={<CompanyTrainingProfilesPage />} path="/company-training-profiles" />
    <Route element={<NotificationsPage />} path="/notifications" />
    <Route
      element={<NotificationPreferencesPage />}
      path="/settings/notifications"
    />
    <Route element={<ReportsHomePage />} path="/reports" />
    <Route element={<ReportsPage />} path="/reports/overview" />
    <Route element={<ReportsPage />} path="/reports/education-performance" />
    <Route
      element={<CertificatePipelineReportPage />}
      path="/reports/certificate-pipeline"
    />
    <Route
      element={<CertificateHierarchyReportPage />}
      path="/reports/certificate-hierarchy"
    />
    <Route
      element={<InternProfileReportPage />}
      path="/reports/intern-profile"
    />
    <Route element={<BudgetReportPage />} path="/reports/budget" />
    <Route
      element={<DepartmentInvestmentReportPage />}
      path="/reports/department-investment"
    />
    <Route element={<SurveyNpsReportPage />} path="/reports/survey-nps" />
    <Route
      element={<VirtualClassroomReportPage />}
      path="/reports/virtual-classroom"
    />
    <Route
      element={<TrainerPerformanceReportPage />}
      path="/reports/trainer-performance"
    />
    <Route element={<ComplianceReportPage />} path="/reports/compliance" />
    <Route
      element={<ExamAnalyticsReportPage />}
      path="/reports/exam-analytics"
    />
    <Route
      element={<LearningPathsReportPage />}
      path="/reports/learning-paths"
    />
    <Route
      element={<ReportsEducationDetailPage />}
      path="/reports/education/:educationId"
    />
    {/* Genişletme — 12 yeni rapor */}
    <Route element={<LoginActivityReportPage />} path="/reports/login-activity" />
    <Route element={<LibraryConsumptionReportPage />} path="/reports/library-consumption" />
    <Route element={<CommunicationEffectivenessReportPage />} path="/reports/communication-effectiveness" />
    <Route element={<SupportSlaReportPage />} path="/reports/support-sla" />
    <Route element={<PersonnelDemographicsReportPage />} path="/reports/personnel-demographics" />
    <Route element={<LocationPerformanceReportPage />} path="/reports/location-performance" />
    <Route element={<AttendanceTrackingReportPage />} path="/reports/attendance-tracking" />
    <Route element={<ExamDifficultyReportPage />} path="/reports/exam-difficulty" />
    <Route element={<CertificateValidityReportPage />} path="/reports/certificate-validity" />
    <Route element={<RoiInvestmentReportPage />} path="/reports/roi-investment" />
    <Route element={<EnrollmentFunnelReportPage />} path="/reports/enrollment-funnel" />
    <Route element={<SurveyCrossTabReportPage />} path="/reports/survey-cross-tab" />
    {/* Add your routes here */}
    {/* Example:
    <Route element={<ExamplePage />} path="/example" />
    */}
    <Route element={<UnauthorizedPage />} path="/unauthorized" />
    <Route element={<NotFoundPage />} path="*" />
  </Routes>
);

export default DashboardRoutes;
